import {
  Hand, MagnifyingGlassPlus, MagnifyingGlassMinus, CornersOut, CornersIn, LineSegment, Circle, Rectangle,
  BezierCurve, ArrowsOut, ArrowClockwise, Copy, Trash, Ruler, TextT, SelectionPlus, ArrowUpRight,
  FloppyDisk, ArrowUUpLeft, ArrowUUpRight, FileArrowDown, ImageSquare, GridNine, NotePencil,
} from "@phosphor-icons/react";
import { LAYER_ORDER, LAYER_STYLES } from "./studioUtils";

const TOOLS = [
  { group: "View", items: [
    { id: "pan", icon: Hand, tip: "Pan (Space+drag)" },
    { id: "zoom_in", icon: MagnifyingGlassPlus, tip: "Zoom in", action: "zoom_in" },
    { id: "zoom_out", icon: MagnifyingGlassMinus, tip: "Zoom out", action: "zoom_out" },
    { id: "fit", icon: CornersOut, tip: "Fit to view", action: "fit" },
  ]},
  { group: "Draw", items: [
    { id: "line", icon: LineSegment, tip: "Line (L)" },
    { id: "circle", icon: Circle, tip: "Circle (C)" },
    { id: "rect", icon: Rectangle, tip: "Rectangle (R)" },
    { id: "polyline", icon: BezierCurve, tip: "Polyline — double-click to finish" },
  ]},
  { group: "Modify", items: [
    { id: "select", icon: ArrowsOut, tip: "Select / Move (Esc)" },
    { id: "rotate", icon: ArrowClockwise, tip: "Rotate selected by angle", action: "rotate" },
    { id: "copy", icon: Copy, tip: "Copy selected (Ctrl+C / Ctrl+V)", action: "copy" },
    { id: "delete", icon: Trash, tip: "Delete selected (Del)", action: "delete" },
  ]},
  { group: "Dimension", items: [
    { id: "dim", icon: Ruler, tip: "Linear dimension — click 2 points, then placement" },
  ]},
  { group: "Annotate", items: [
    { id: "text", icon: TextT, tip: "Text (T)" },
    { id: "markup_pin", icon: NotePencil, tip: "Markup pin — click to place a comment pin (assignable, tracked in the markups list)", label: "Markup" },
    { id: "markup_circle", icon: SelectionPlus, tip: "Markup circle (red, on MARKUP layer)" },
    { id: "markup_arrow", icon: ArrowUpRight, tip: "Markup arrow (red, on MARKUP layer)" },
  ]},
  { group: "Actions", items: [
    { id: "save", icon: FloppyDisk, tip: "Save (Ctrl+S)", action: "save" },
    { id: "undo", icon: ArrowUUpLeft, tip: "Undo (Ctrl+Z)", action: "undo" },
    { id: "redo", icon: ArrowUUpRight, tip: "Redo (Ctrl+Y)", action: "redo" },
    { id: "export_dxf", icon: FileArrowDown, tip: "Export DXF", action: "export_dxf" },
    { id: "export_pdf", icon: FileArrowDown, tip: "Export PDF", action: "export_pdf" },
    { id: "export_png", icon: ImageSquare, tip: "Export PNG snapshot", action: "export_png" },
    { id: "grid", icon: GridNine, tip: "Toggle grid", action: "grid" },
  ]},
];

export default function Toolbar({ tool, setTool, onAction, disabled, locked, revisions, revId, onRevChange, layerVis, activeLayer, onToggleLayer, onSetActiveLayer, zoomPct, onToggleFs, isFs }) {
  const Btn = ({ it }) => (
    <button key={it.id} data-testid={`tool-${it.id}`} title={it.tip} disabled={disabled || (locked && !["pan", "zoom_in", "zoom_out", "fit", "export_dxf", "export_pdf", "export_png", "grid"].includes(it.id))}
      onClick={() => (it.action ? onAction(it.action) : setTool(it.id))}
      className={`h-8 ${it.label ? "px-2 gap-1" : "w-8"} flex items-center justify-center rounded-sm transition-colors disabled:opacity-30 ${
        tool === it.id ? "bg-[var(--c-accent)] text-white" : "text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-border)]"}`}>
      <it.icon size={15} />
      {it.label && <span className="text-[10px] font-medium">{it.label}</span>}
    </button>
  );

  return (
    <div className="flex items-center gap-1 px-2 h-11 bg-[var(--c-surface)] border-b border-[var(--c-border)] overflow-x-auto shrink-0" data-testid="studio-toolbar">
      {TOOLS.map((g, gi) => (
        <div key={g.group} className={`flex items-center gap-0.5 ${gi ? "pl-2 ml-1 border-l border-[var(--c-border)]" : ""}`}>
          {g.items.map((it) => <Btn key={it.id} it={it} />)}
        </div>
      ))}
      <span className="font-mono text-[10px] text-[var(--c-muted)] ml-1 shrink-0" data-testid="zoom-display">{zoomPct}%</span>
      {onToggleFs && (
        <button data-testid="fullscreen-btn" title={isFs ? "Exit full screen" : "Full screen — canvas + hierarchy fill the screen"} onClick={onToggleFs}
          className="h-8 px-2 flex items-center justify-center gap-1 rounded-sm text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-border)] transition-colors shrink-0">
          {isFs ? <CornersIn size={15} /> : <CornersOut size={15} />}
          <span className="text-[10px] font-medium">{isFs ? "Exit full screen" : "Full screen"}</span>
        </button>
      )}

      <div className="flex items-center gap-1 pl-2 ml-1 border-l border-[var(--c-border)]" data-testid="layer-controls">
        {LAYER_ORDER.map((ln) => (
          <button key={ln} data-testid={`layer-${ln}`} title={`${ln} — ${LAYER_STYLES[ln].label}`}
            onClick={() => onToggleLayer(ln)} onDoubleClick={() => onSetActiveLayer(ln)}
            className={`flex items-center gap-1 px-1.5 h-7 rounded-sm text-[9px] font-mono ${
              activeLayer === ln ? "bg-[var(--c-accent)]/20 text-[var(--c-text)]" : "text-[var(--c-muted)] hover:text-[var(--c-text)]"}`}>
            <span className="w-2.5 h-2.5 rounded-[2px] border"
              style={{ background: layerVis[ln] !== false ? LAYER_STYLES[ln].color : "transparent", borderColor: LAYER_STYLES[ln].color }} />
            {ln.slice(0, 3)}
          </button>
        ))}
      </div>

      {revisions.length > 0 && (
        <select data-testid="rev-select" value={revId || ""} onChange={(e) => onRevChange(e.target.value)}
          className="ml-auto h-7 bg-[var(--c-bg)] border border-[var(--c-border)] text-[var(--c-text)] text-[11px] rounded-sm px-2 shrink-0">
          {revisions.map((r) => (
            <option key={r.id} value={r.id}>
              Rev {r.rev_label || r.revision} ({r.status}){r.current ? " — Current" : ""}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}
